import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faPencilAlt, faTrash, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { DatePickerInput } from '@mantine/dates';
import { API_ENDPOINTS } from '../constants/api';
import { formatDisplayDate } from '../utils/dateFormat';
import {
  formatTillageForDisplay,
  normalizeTillage,
  TILLAGE_STORAGE_VALUES,
} from '../utils/tillageDisplay';
import './CropPickerModal.css';

interface CropPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  fieldId: string;
  fieldName: string;
  fieldAcres: number;
  year: number;
  userToken: string;
  onCropAdded: () => void;
  mode: 'add' | 'edit';
  selectedOrganizationId?: string;
  engagementLevel?: number;
  readonlyCropType?: boolean;
  existingCropData?: {
    planting_id: string;
    crop_id: string;
    acres: string;
    planting_date?: string;
    harvest_date?: string;
    yield?: number;
    bushelsPerAcre?: number;
    expected_yield?: number | null;
    total_synthetic_n?: number | null;
    tillage?: string | null;
    cover_crop_used?: string;
    tdisc_designation?: string | null;
    tdisc_calculated_at?: string | null;
    tdisc_pending?: boolean;
    tillage_event_count?: number;
  };
}

interface Crop {
  crop_id: string;
  crop: string;
  sort_order?: number;
}

interface Fertilizer {
  fertilizer_id: string;
  fertilizer: string;
  fertilizer_type?: string | null;
  nitrogen?: number | null;
  phosphorus?: number | null;
  potassium?: number | null;
}

interface Manure {
  manure_id: string;
  manure: string;
  form: 'solid' | 'liquid';
  sort_order: number;
}

interface FertilizerApplication {
  fertilizer_application_id: string;
  planting_id: string;
  fertilizer_id: string;
  the_date: string;
  rate: number;
  acres: number;
  nitrification_inhibitor_percentage?: number | null;
  fertilizer?: Fertilizer;
}

interface ManureApplication {
  manure_application_id: string;
  planting_id: string;
  manure_id: string;
  the_date: string;
  rate: number;
  acres: number;
  n_content: number;
  manure?: Manure;
}

interface TdiscImplementMapping {
  sequence: number;
  tdisc_implement_id: string;
  implement_name: string;
}

interface TdiscOperation {
  tdisc_operation_id: string;
  description: string;
  sort_order: number;
  implements: TdiscImplementMapping[];
}

interface TillageEvent {
  tillage_event_id: string;
  planting_id: string;
  the_date: string;
  tillage_window: string;
  tdisc_operation_id: string;
  description: string;
  implements: string[];
  expanded_row_count: number;
}

type ActiveTab = 'crop' | 'fertilizer' | 'manure' | 'tillage';

const TILLAGE_WINDOWS = [
  { value: 'Field_Preparation', label: 'Field Preparation' },
  { value: 'Before_Planting', label: 'Before Planting' },
  { value: 'Planting', label: 'Planting' },
  { value: 'After_Planting', label: 'After Planting' },
  { value: 'Harvest', label: 'Harvest' },
] as const;

const TILLAGE_WINDOW_LABELS: Record<string, string> = Object.fromEntries(
  TILLAGE_WINDOWS.map((w) => [w.value, w.label]),
);

const parseIsoDate = (value: string): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const toIsoDate = (date: Date | null): string => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// FD-CIC Crop Inputs tillage dropdown values (storage keys)
const TILLAGE_OPTIONS = [...TILLAGE_STORAGE_VALUES];
// FD-CIC Crop Inputs cover crop dropdown values
const COVER_CROP_OPTIONS = [
  'No cover crop',
  'Cover crop',
  'Cover crop with grazing',
];

/** Map legacy cover-crop labels to FD-CIC dropdown values. */
const normalizeCoverCrop = (value?: string | null): string => {
  if (!value) return 'No cover crop';
  const v = value.trim().toLowerCase();
  if (v === 'no cover crop' || v === 'no cover') return 'No cover crop';
  if (v.includes('grazing')) return 'Cover crop with grazing';
  if (v.startsWith('cover crop')) return 'Cover crop';
  return COVER_CROP_OPTIONS.includes(value) ? value : 'No cover crop';
};

const orgQuery = (selectedOrganizationId?: string) =>
  selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';

const CropPickerModal: React.FC<CropPickerModalProps> = ({
  isOpen,
  onClose,
  fieldId,
  fieldName,
  fieldAcres,
  year,
  userToken,
  onCropAdded,
  mode,
  selectedOrganizationId,
  engagementLevel = 1,
  readonlyCropType = false,
  existingCropData,
}) => {
  const [crops, setCrops] = useState<Crop[]>([]);
  const [selectedCropId, setSelectedCropId] = useState('');
  const [plantingDate, setPlantingDate] = useState('');
  const [acres, setAcres] = useState(fieldAcres.toString());
  const [harvestDate, setHarvestDate] = useState('');
  const [harvestYield, setHarvestYield] = useState('');
  const [yieldPerAcre, setYieldPerAcre] = useState('');
  const [expectedYield, setExpectedYield] = useState('');
  const [totalSyntheticN, setTotalSyntheticN] = useState<number | null>(null);
  const [tillage, setTillage] = useState('');
  const [tdiscDesignation, setTdiscDesignation] = useState<string | null>(null);
  const [tdiscCalculatedAt, setTdiscCalculatedAt] = useState<string | null>(null);
  const [coverCropUsed, setCoverCropUsed] = useState('No cover crop');
  const [activeTab, setActiveTab] = useState<ActiveTab>('crop');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [fertilizers, setFertilizers] = useState<Fertilizer[]>([]);
  const [manures, setManures] = useState<Manure[]>([]);
  const [fertilizerApps, setFertilizerApps] = useState<FertilizerApplication[]>([]);
  const [manureApps, setManureApps] = useState<ManureApplication[]>([]);
  const [tdiscOperations, setTdiscOperations] = useState<TdiscOperation[]>([]);
  const [tillageEvents, setTillageEvents] = useState<TillageEvent[]>([]);
  const [expandedRowCount, setExpandedRowCount] = useState(0);
  const [appsLoading, setAppsLoading] = useState(false);

  const [fertAppModalOpen, setFertAppModalOpen] = useState(false);
  const [editingFertApp, setEditingFertApp] = useState<FertilizerApplication | null>(null);
  const [fertForm, setFertForm] = useState({
    fertilizer_id: '',
    the_date: '',
    rate: '',
    acres: '',
    nitrification_inhibitor_percentage: '',
  });
  const [fertFormSaving, setFertFormSaving] = useState(false);

  const [manureAppModalOpen, setManureAppModalOpen] = useState(false);
  const [editingManureApp, setEditingManureApp] = useState<ManureApplication | null>(null);
  const [manureForm, setManureForm] = useState({
    manure_id: '',
    the_date: '',
    rate: '',
    acres: '',
    n_content: '',
  });
  const [manureFormSaving, setManureFormSaving] = useState(false);

  const [tillageEventModalOpen, setTillageEventModalOpen] = useState(false);
  const [editingTillageEvent, setEditingTillageEvent] =
    useState<TillageEvent | null>(null);
  const [tillageForm, setTillageForm] = useState({
    the_date: '',
    tillage_window: 'Field_Preparation',
    tdisc_operation_id: '',
  });
  const [operationSearch, setOperationSearch] = useState('');
  const [tillageFormSaving, setTillageFormSaving] = useState(false);

  // FD-CIC: all crops including Canola use bushels/acre (GREET used lbs/acre for Canola)
  const yieldPerAcreLabel = 'Yield per Acre (bushels/acre):';
  const totalYieldLabel = 'Total Yield (bushels):';
  const expectedYieldLabel = 'Expected Yield (bushels/acre):';
  const yieldPerAcrePlaceholder = 'Bushels/acre';
  const totalYieldPlaceholder = 'Total bushels';

  const plantingId = existingCropData?.planting_id;
  const canManageApplications = mode === 'edit' && !!plantingId;
  const hasTillageEvents = tillageEvents.length > 0;
  const tdiscPending =
    hasTillageEvents && !tdiscCalculatedAt;
  const selectedTillageOperation = tdiscOperations.find(
    (op) => op.tdisc_operation_id === tillageForm.tdisc_operation_id,
  );
  const selectedOperationImplementCount =
    selectedTillageOperation?.implements.length || 0;
  const editingEventRowCount = editingTillageEvent?.expanded_row_count || 0;
  const projectedExpandedRows =
    expandedRowCount -
    editingEventRowCount +
    selectedOperationImplementCount;
  const filteredTdiscOperations = (() => {
    const search = operationSearch.trim().toLowerCase();
    const filtered = tdiscOperations.filter((op) =>
      op.description.toLowerCase().includes(search),
    );
    // Keep the current selection visible in the list even if search hides it
    if (
      selectedTillageOperation &&
      !filtered.some(
        (op) =>
          op.tdisc_operation_id === selectedTillageOperation.tdisc_operation_id,
      )
    ) {
      return [selectedTillageOperation, ...filtered];
    }
    return filtered;
  })();

  const derivedTotalSyntheticN = (() => {
    if (!canManageApplications) return totalSyntheticN;
    if (appsLoading) return totalSyntheticN;
    if (fertilizerApps.length === 0) return null;

    const plantingAcres = parseFloat(acres) || 0;
    let totalNLbs = 0;
    let treatedAcres = 0;
    for (const app of fertilizerApps) {
      const rate = Number(app.rate) || 0;
      const appAcres = Number(app.acres) || 0;
      const nPct =
        app.fertilizer?.nitrogen != null ? Number(app.fertilizer.nitrogen) : 0;
      totalNLbs += rate * (nPct / 100) * appAcres;
      treatedAcres += appAcres;
    }
    const divisor = plantingAcres > 0 ? plantingAcres : treatedAcres;
    if (divisor <= 0) return null;
    return Math.round((totalNLbs / divisor) * 100) / 100;
  })();

  const currentModeRef = useRef(mode);
  const existingCropDataRef = useRef(existingCropData);

  useEffect(() => {
    currentModeRef.current = mode;
    existingCropDataRef.current = existingCropData;
  }, [mode, existingCropData]);

  const roundToTwoDecimals = (value: number): string => {
    return (Math.round(value * 100) / 100).toFixed(2);
  };

  useEffect(() => {
    if (isOpen) {
      loadCrops();
      setActiveTab('crop');
      setError('');
      if (mode === 'edit' && existingCropData) {
        const existingAcres = parseFloat(existingCropData.acres.toString()) || 0;
        const existingTotalYield = existingCropData.yield ?? 0;
        const existingBushelsPerAcre = existingCropData.bushelsPerAcre ?? 0;

        let initializedTotalYield = '';
        let initializedYieldPerAcre = '';

        if (existingTotalYield > 0) {
          initializedTotalYield = roundToTwoDecimals(existingTotalYield);
          if (existingAcres > 0) {
            initializedYieldPerAcre = roundToTwoDecimals(existingTotalYield / existingAcres);
          }
        } else if (existingBushelsPerAcre > 0) {
          initializedYieldPerAcre = roundToTwoDecimals(existingBushelsPerAcre);
          if (existingAcres > 0) {
            initializedTotalYield = roundToTwoDecimals(existingBushelsPerAcre * existingAcres);
          }
        }

        setSelectedCropId(existingCropData.crop_id);
        setAcres(existingCropData.acres.toString());
        setPlantingDate(
          existingCropData.planting_date
            ? existingCropData.planting_date.split('T')[0]
            : '',
        );
        setHarvestDate(
          existingCropData.harvest_date
            ? existingCropData.harvest_date.split('T')[0]
            : '',
        );
        setHarvestYield(initializedTotalYield);
        setYieldPerAcre(initializedYieldPerAcre);
        setExpectedYield(
          existingCropData.expected_yield != null
            ? roundToTwoDecimals(Number(existingCropData.expected_yield))
            : '',
        );
        setTotalSyntheticN(
          existingCropData.total_synthetic_n != null
            ? Number(existingCropData.total_synthetic_n)
            : null,
        );
        setTillage(normalizeTillage(existingCropData.tillage));
        setTdiscDesignation(existingCropData.tdisc_designation || null);
        setTdiscCalculatedAt(existingCropData.tdisc_calculated_at || null);
        setCoverCropUsed(
          normalizeCoverCrop(existingCropData.cover_crop_used),
        );
      } else {
        setSelectedCropId('');
        setAcres(fieldAcres.toString());
        setPlantingDate('');
        setHarvestDate('');
        setHarvestYield('');
        setYieldPerAcre('');
        setExpectedYield('');
        setTotalSyntheticN(null);
        setTillage('');
        setTdiscDesignation(null);
        setTdiscCalculatedAt(null);
        setCoverCropUsed('No cover crop');
      }
    }
  }, [isOpen, fieldAcres, mode, existingCropData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen && canManageApplications) {
      setAppsLoading(true);
      loadLookups();
      loadApplications();
      loadTillageEvents();
    } else if (!isOpen) {
      setFertilizerApps([]);
      setManureApps([]);
      setTillageEvents([]);
      setExpandedRowCount(0);
      setTdiscOperations([]);
      setAppsLoading(false);
    }
  }, [isOpen, canManageApplications, plantingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTotalYieldChange = (value: string) => {
    setHarvestYield(value);
    if (value && acres) {
      const totalYield = parseFloat(value);
      const acresValue = parseFloat(acres);
      if (totalYield > 0 && acresValue > 0) {
        setYieldPerAcre(roundToTwoDecimals(totalYield / acresValue));
      } else {
        setYieldPerAcre('');
      }
    } else {
      setYieldPerAcre('');
    }
  };

  const handleYieldPerAcreChange = (value: string) => {
    setYieldPerAcre(value);
    if (value && acres) {
      const yieldPerAcreValue = parseFloat(value);
      const acresValue = parseFloat(acres);
      if (yieldPerAcreValue > 0 && acresValue > 0) {
        setHarvestYield(roundToTwoDecimals(yieldPerAcreValue * acresValue));
      } else {
        setHarvestYield('');
      }
    } else {
      setHarvestYield('');
    }
  };

  const handleAcresChange = (value: string) => {
    setAcres(value);
    if (value && harvestYield) {
      const acresValue = parseFloat(value);
      const totalYield = parseFloat(harvestYield);
      if (acresValue > 0 && totalYield > 0) {
        setYieldPerAcre(roundToTwoDecimals(totalYield / acresValue));
      } else {
        setYieldPerAcre('');
      }
    } else if (value && yieldPerAcre) {
      const acresValue = parseFloat(value);
      const yieldPerAcreValue = parseFloat(yieldPerAcre);
      if (acresValue > 0 && yieldPerAcreValue > 0) {
        setHarvestYield(roundToTwoDecimals(yieldPerAcreValue * acresValue));
      } else {
        setHarvestYield('');
      }
    }
  };

  const loadCrops = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CROPS, {
        headers: { Authorization: `Bearer ${userToken}` },
      });

      if (response.ok) {
        const cropsData = await response.json();
        const sortedCrops = cropsData.sort((a: Crop, b: Crop) => {
          if (a.sort_order !== undefined && b.sort_order !== undefined) {
            return a.sort_order - b.sort_order;
          }
          return a.crop.localeCompare(b.crop);
        });
        setCrops(sortedCrops);
        if (
          currentModeRef.current === 'add' &&
          sortedCrops.length > 0 &&
          !existingCropDataRef.current
        ) {
          setSelectedCropId(sortedCrops[0].crop_id);
        }
      }
    } catch (err) {
      console.error('Error loading crops:', err);
      setError('Failed to load crops');
    }
  };

  const loadLookups = async () => {
    try {
      const [fertRes, manureRes, tdiscRes] = await Promise.all([
        fetch(API_ENDPOINTS.FERTILIZERS, {
          headers: { Authorization: `Bearer ${userToken}` },
        }),
        fetch(API_ENDPOINTS.MANURES, {
          headers: { Authorization: `Bearer ${userToken}` },
        }),
        fetch(API_ENDPOINTS.TDISC_OPERATIONS, {
          headers: { Authorization: `Bearer ${userToken}` },
        }),
      ]);
      if (fertRes.ok) setFertilizers(await fertRes.json());
      if (manureRes.ok) setManures(await manureRes.json());
      if (tdiscRes.ok) setTdiscOperations(await tdiscRes.json());
    } catch (err) {
      console.error('Error loading fertilizer/manure/tdisc lookups:', err);
    }
  };

  const loadApplications = async () => {
    if (!plantingId) return;
    setAppsLoading(true);
    try {
      const q = orgQuery(selectedOrganizationId);
      const plantingParam = `planting_id=${plantingId}`;
      const fertUrl = `${API_ENDPOINTS.FERTILIZER_APPLICATIONS}${q ? `${q}&` : '?'}${plantingParam}`;
      const manureUrl = `${API_ENDPOINTS.MANURE_APPLICATIONS}${q ? `${q}&` : '?'}${plantingParam}`;

      const [fertRes, manureRes] = await Promise.all([
        fetch(fertUrl, { headers: { Authorization: `Bearer ${userToken}` } }),
        fetch(manureUrl, { headers: { Authorization: `Bearer ${userToken}` } }),
      ]);
      if (fertRes.ok) setFertilizerApps(await fertRes.json());
      if (manureRes.ok) setManureApps(await manureRes.json());
    } catch (err) {
      console.error('Error loading applications:', err);
    } finally {
      setAppsLoading(false);
    }
  };

  const loadTillageEvents = async () => {
    if (!plantingId) return;
    try {
      const q = orgQuery(selectedOrganizationId);
      const url = `${API_ENDPOINTS.TILLAGE_EVENTS}${q ? `${q}&` : '?'}planting_id=${plantingId}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTillageEvents(data.events || []);
        setExpandedRowCount(data.expanded_row_count || 0);
        // Always prefer server tillage/designation over possibly stale grid props
        if (data.tillage != null) {
          setTillage(normalizeTillage(data.tillage));
        }
        setTdiscDesignation(data.tdisc_designation || null);
        setTdiscCalculatedAt(data.tdisc_calculated_at || null);
      }
    } catch (err) {
      console.error('Error loading tillage events:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCropId || !acres) {
      setError('Please select a crop and enter acres');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const requestBody: Record<string, unknown> = {
        field_id: fieldId,
        year: year,
        crop_id: selectedCropId,
        acres: parseFloat(acres),
        cover_crop_used: coverCropUsed,
      };

      if (plantingDate) requestBody.planting_date = plantingDate;
      if (harvestDate) requestBody.harvest_date = harvestDate;
      if (harvestYield)
        requestBody.yield = Math.round(parseFloat(harvestYield) * 100) / 100;
      if (expectedYield !== '')
        requestBody.expected_yield =
          Math.round(parseFloat(expectedYield) * 100) / 100;
      // Once tillage events exist, T-DISC owns tillage — do not overwrite with manual dropdown
      if (!hasTillageEvents && tillage) requestBody.tillage = tillage;

      const url =
        mode === 'edit' && existingCropData
          ? `${API_ENDPOINTS.CROPS}/cropped-field/${existingCropData.planting_id}`
          : `${API_ENDPOINTS.CROPS}/cropped-field`;

      const finalUrl = url + orgQuery(selectedOrganizationId);

      const response = await fetch(finalUrl, {
        method: mode === 'edit' ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        onCropAdded();
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.message || `Failed to ${mode} crop`);
      }
    } catch (err) {
      console.error(`Error ${mode}ing crop:`, err);
      setError(`Failed to ${mode} crop`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!existingCropData?.planting_id) {
      setError('No crop data to delete');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      const url = `${API_ENDPOINTS.CROPS}/cropped-field/${existingCropData.planting_id}${orgQuery(selectedOrganizationId)}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${userToken}` },
      });

      if (response.ok) {
        onCropAdded();
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete crop');
      }
    } catch (err) {
      console.error('Error deleting crop:', err);
      setError('Failed to delete crop');
    } finally {
      setIsDeleting(false);
    }
  };

  const openAddFertApp = () => {
    setEditingFertApp(null);
    setFertForm({
      fertilizer_id: fertilizers[0]?.fertilizer_id || '',
      the_date: '',
      rate: '',
      acres: acres || '',
      nitrification_inhibitor_percentage: '',
    });
    setFertAppModalOpen(true);
  };

  const openEditFertApp = (app: FertilizerApplication) => {
    setEditingFertApp(app);
    setFertForm({
      fertilizer_id: app.fertilizer_id,
      the_date: app.the_date ? String(app.the_date).split('T')[0] : '',
      rate: String(app.rate),
      acres: String(app.acres),
      nitrification_inhibitor_percentage:
        app.nitrification_inhibitor_percentage != null
          ? String(app.nitrification_inhibitor_percentage)
          : '',
    });
    setFertAppModalOpen(true);
  };

  const saveFertApp = async () => {
    if (!plantingId || !fertForm.fertilizer_id || !fertForm.the_date || !fertForm.rate || !fertForm.acres) {
      setError('Please fill all fertilizer application fields');
      return;
    }
    setFertFormSaving(true);
    setError('');
    try {
      const niValue =
        fertForm.nitrification_inhibitor_percentage === ''
          ? null
          : parseFloat(fertForm.nitrification_inhibitor_percentage);
      const body = {
        planting_id: plantingId,
        fertilizer_id: fertForm.fertilizer_id,
        the_date: fertForm.the_date,
        rate: parseFloat(fertForm.rate),
        acres: parseFloat(fertForm.acres),
        nitrification_inhibitor_percentage: niValue,
      };
      const url = editingFertApp
        ? `${API_ENDPOINTS.FERTILIZER_APPLICATIONS}/${editingFertApp.fertilizer_application_id}${orgQuery(selectedOrganizationId)}`
        : `${API_ENDPOINTS.FERTILIZER_APPLICATIONS}${orgQuery(selectedOrganizationId)}`;
      const response = await fetch(url, {
        method: editingFertApp ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(
          editingFertApp
            ? {
                fertilizer_id: body.fertilizer_id,
                the_date: body.the_date,
                rate: body.rate,
                acres: body.acres,
                nitrification_inhibitor_percentage:
                  body.nitrification_inhibitor_percentage,
              }
            : body,
        ),
      });
      if (response.ok) {
        setFertAppModalOpen(false);
        await loadApplications();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save fertilizer application');
      }
    } catch (err) {
      console.error('Error saving fertilizer application:', err);
      setError('Failed to save fertilizer application');
    } finally {
      setFertFormSaving(false);
    }
  };

  const deleteFertApp = async (id: string) => {
    if (!window.confirm('Delete this fertilizer application?')) return;
    try {
      const response = await fetch(
        `${API_ENDPOINTS.FERTILIZER_APPLICATIONS}/${id}${orgQuery(selectedOrganizationId)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${userToken}` },
        },
      );
      if (response.ok) {
        await loadApplications();
      } else setError('Failed to delete fertilizer application');
    } catch (err) {
      console.error('Error deleting fertilizer application:', err);
      setError('Failed to delete fertilizer application');
    }
  };

  const selectedManure = manures.find((m) => m.manure_id === manureForm.manure_id);
  const nContentUnit =
    selectedManure?.form === 'liquid' ? 'lbs N/1000 gal' : 'lbs N/ton';
  const manureRateUnit =
    selectedManure?.form === 'liquid' ? '1000 gal/acre' : 'tons/acre';

  const openAddManureApp = () => {
    setEditingManureApp(null);
    setManureForm({
      manure_id: manures[0]?.manure_id || '',
      the_date: '',
      rate: '',
      acres: acres || '',
      n_content: '',
    });
    setManureAppModalOpen(true);
  };

  const openEditManureApp = (app: ManureApplication) => {
    setEditingManureApp(app);
    setManureForm({
      manure_id: app.manure_id,
      the_date: app.the_date ? String(app.the_date).split('T')[0] : '',
      rate: String(app.rate),
      acres: String(app.acres),
      n_content: String(app.n_content),
    });
    setManureAppModalOpen(true);
  };

  const saveManureApp = async () => {
    if (
      !plantingId ||
      !manureForm.manure_id ||
      !manureForm.the_date ||
      !manureForm.rate ||
      !manureForm.acres ||
      !manureForm.n_content
    ) {
      setError('Please fill all manure application fields');
      return;
    }
    setManureFormSaving(true);
    setError('');
    try {
      const body = {
        planting_id: plantingId,
        manure_id: manureForm.manure_id,
        the_date: manureForm.the_date,
        rate: parseFloat(manureForm.rate),
        acres: parseFloat(manureForm.acres),
        n_content: parseFloat(manureForm.n_content),
      };
      const url = editingManureApp
        ? `${API_ENDPOINTS.MANURE_APPLICATIONS}/${editingManureApp.manure_application_id}${orgQuery(selectedOrganizationId)}`
        : `${API_ENDPOINTS.MANURE_APPLICATIONS}${orgQuery(selectedOrganizationId)}`;
      const response = await fetch(url, {
        method: editingManureApp ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(
          editingManureApp
            ? {
                manure_id: body.manure_id,
                the_date: body.the_date,
                rate: body.rate,
                acres: body.acres,
                n_content: body.n_content,
              }
            : body,
        ),
      });
      if (response.ok) {
        setManureAppModalOpen(false);
        await loadApplications();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save manure application');
      }
    } catch (err) {
      console.error('Error saving manure application:', err);
      setError('Failed to save manure application');
    } finally {
      setManureFormSaving(false);
    }
  };

  const deleteManureApp = async (id: string) => {
    if (!window.confirm('Delete this manure application?')) return;
    try {
      const response = await fetch(
        `${API_ENDPOINTS.MANURE_APPLICATIONS}/${id}${orgQuery(selectedOrganizationId)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${userToken}` },
        },
      );
      if (response.ok) await loadApplications();
      else setError('Failed to delete manure application');
    } catch (err) {
      console.error('Error deleting manure application:', err);
      setError('Failed to delete manure application');
    }
  };

  const openAddTillageEvent = () => {
    setEditingTillageEvent(null);
    setOperationSearch('');
    setTillageForm({
      the_date: '',
      tillage_window: 'Field_Preparation',
      tdisc_operation_id: tdiscOperations[0]?.tdisc_operation_id || '',
    });
    setTillageEventModalOpen(true);
  };

  const openEditTillageEvent = (event: TillageEvent) => {
    setEditingTillageEvent(event);
    setOperationSearch('');
    setTillageForm({
      the_date: event.the_date ? String(event.the_date).split('T')[0] : '',
      tillage_window: event.tillage_window,
      tdisc_operation_id: event.tdisc_operation_id,
    });
    setTillageEventModalOpen(true);
  };

  const saveTillageEvent = async () => {
    if (
      !plantingId ||
      !tillageForm.the_date ||
      !tillageForm.tillage_window ||
      !tillageForm.tdisc_operation_id
    ) {
      setError('Please fill all tillage event fields');
      return;
    }
    if (projectedExpandedRows > 20) {
      setError(
        `Expanded T-DISC operations would be ${projectedExpandedRows} / 20. Remove or simplify events before saving.`,
      );
      return;
    }
    setTillageFormSaving(true);
    setError('');
    try {
      const url = editingTillageEvent
        ? `${API_ENDPOINTS.TILLAGE_EVENTS}/${editingTillageEvent.tillage_event_id}${orgQuery(selectedOrganizationId)}`
        : `${API_ENDPOINTS.TILLAGE_EVENTS}${orgQuery(selectedOrganizationId)}`;
      const response = await fetch(url, {
        method: editingTillageEvent ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(
          editingTillageEvent
            ? {
                the_date: tillageForm.the_date,
                tillage_window: tillageForm.tillage_window,
                tdisc_operation_id: tillageForm.tdisc_operation_id,
              }
            : {
                planting_id: plantingId,
                the_date: tillageForm.the_date,
                tillage_window: tillageForm.tillage_window,
                tdisc_operation_id: tillageForm.tdisc_operation_id,
              },
        ),
      });
      if (response.ok) {
        setTillageEventModalOpen(false);
        setTdiscCalculatedAt(null);
        await loadTillageEvents();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Failed to save tillage event');
      }
    } catch (err) {
      console.error('Error saving tillage event:', err);
      setError('Failed to save tillage event');
    } finally {
      setTillageFormSaving(false);
    }
  };

  const deleteTillageEvent = async (id: string) => {
    if (!window.confirm('Delete this tillage event?')) return;
    try {
      const response = await fetch(
        `${API_ENDPOINTS.TILLAGE_EVENTS}/${id}${orgQuery(selectedOrganizationId)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${userToken}` },
        },
      );
      if (response.ok) {
        setTdiscCalculatedAt(null);
        await loadTillageEvents();
      } else {
        setError('Failed to delete tillage event');
      }
    } catch (err) {
      console.error('Error deleting tillage event:', err);
      setError('Failed to delete tillage event');
    }
  };

  const ApplicationDateField = ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (isoDate: string) => void;
  }) => (
    <DatePickerInput
      className="application-date-picker"
      value={parseIsoDate(value)}
      onChange={(date) => onChange(toIsoDate(date))}
      valueFormat="D MMM YYYY"
      clearable
      firstDayOfWeek={0}
      dropdownType="popover"
      popoverProps={{ withinPortal: true, zIndex: 3000 }}
      rightSection={
        <FontAwesomeIcon icon={faCalendarAlt} aria-hidden="true" />
      }
      aria-label="Choose date"
    />
  );

  if (!isOpen) return null;

  return (
    <div className="crop-picker-modal-overlay">
      <div className="crop-picker-modal crop-picker-modal--wide">
        <div className="crop-picker-modal-header">
          <h3>
            {mode === 'edit' ? 'Edit' : 'Add'} Crop for {fieldName}
          </h3>
          <button className="close-button" onClick={onClose} type="button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="tab-navigation">
          <button
            type="button"
            className={`tab-button ${activeTab === 'crop' ? 'active' : ''}`}
            onClick={() => setActiveTab('crop')}
          >
            Crop Information
          </button>
          {canManageApplications && (
            <>
              <button
                type="button"
                className={`tab-button ${activeTab === 'fertilizer' ? 'active' : ''}`}
                onClick={() => setActiveTab('fertilizer')}
              >
                Fertilizer Applications
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'manure' ? 'active' : ''}`}
                onClick={() => setActiveTab('manure')}
              >
                Manure Applications
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'tillage' ? 'active' : ''}`}
                onClick={() => setActiveTab('tillage')}
              >
                Tillage Events
              </button>
            </>
          )}
        </div>

        {activeTab === 'crop' && (
          <form onSubmit={handleSubmit} className="crop-picker-form">
            <div className="tab-content">
              <div className="form-group">
                <label htmlFor="crop-select">Crop:</label>
                <select
                  id="crop-select"
                  value={selectedCropId}
                  onChange={(e) => setSelectedCropId(e.target.value)}
                  required
                  disabled={readonlyCropType || mode === 'edit'}
                >
                  {crops.map((crop) => (
                    <option key={crop.crop_id} value={crop.crop_id}>
                      {crop.crop}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="acres">Acres:</label>
                <input
                  type="number"
                  id="acres"
                  value={acres}
                  onChange={(e) => handleAcresChange(e.target.value)}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="planting-date">Planting Date:</label>
                <ApplicationDateField
                  value={plantingDate}
                  onChange={setPlantingDate}
                />
              </div>

              {engagementLevel > 1 && (
                <div className="form-group">
                  <label htmlFor="harvest-date">Harvest Date:</label>
                  <ApplicationDateField
                    value={harvestDate}
                    onChange={setHarvestDate}
                  />
                </div>
              )}

              {engagementLevel > 1 && (
                <div className="form-group yield-inputs-row">
                  <div className="yield-input-group">
                    <label htmlFor="yield-per-acre">{yieldPerAcreLabel}</label>
                    <input
                      type="number"
                      id="yield-per-acre"
                      value={yieldPerAcre}
                      onChange={(e) => handleYieldPerAcreChange(e.target.value)}
                      step="0.01"
                      min="0"
                      placeholder={yieldPerAcrePlaceholder}
                    />
                  </div>
                  <div className="yield-input-group">
                    <label htmlFor="harvest-yield">{totalYieldLabel}</label>
                    <input
                      type="number"
                      id="harvest-yield"
                      value={harvestYield}
                      onChange={(e) => handleTotalYieldChange(e.target.value)}
                      step="0.01"
                      min="0"
                      placeholder={totalYieldPlaceholder}
                    />
                  </div>
                </div>
              )}
              {engagementLevel > 1 && (
                <div className="yield-helper-text">
                  Enter either total yield or yield per acre - the other will
                  calculate automatically
                </div>
              )}

              {engagementLevel > 1 && (
                <>
                  <div className="form-group">
                    <label htmlFor="tillage">Tillage Method:</label>
                    {hasTillageEvents ? (
                      <>
                        <input
                          id="tillage"
                          type="text"
                          value={
                            tillage
                              ? `${formatTillageForDisplay(tillage)}${
                                  tdiscDesignation
                                    ? ` (${tdiscDesignation})`
                                    : ''
                                }`
                              : 'Derived by T-DISC'
                          }
                          readOnly
                          disabled
                          title="Derived by T-DISC from tillage events"
                        />
                        <div className="field-helper-text">
                          Derived by T-DISC from Tillage Events.
                          {tdiscPending
                            ? ' Pending carbon recalculation.'
                            : tdiscCalculatedAt
                              ? ' Run Calculate Carbon to refresh after event changes.'
                              : null}
                        </div>
                      </>
                    ) : (
                      <>
                        <select
                          id="tillage"
                          value={tillage}
                          onChange={(e) => setTillage(e.target.value)}
                        >
                          <option value="">Select tillage method</option>
                          {TILLAGE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {formatTillageForDisplay(opt)}
                            </option>
                          ))}
                        </select>
                        <div className="field-helper-text">
                          Manual fallback until tillage events are added. Events
                          drive T-DISC designation on Calculate Carbon.
                        </div>
                      </>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="expected-yield">{expectedYieldLabel}</label>
                    <input
                      type="number"
                      id="expected-yield"
                      value={expectedYield}
                      onChange={(e) => setExpectedYield(e.target.value)}
                      step="0.01"
                      min="0"
                      placeholder={yieldPerAcrePlaceholder}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="total-synthetic-n">
                      Total Synthetic N (lbs N/acre):
                    </label>
                    <input
                      type="text"
                      id="total-synthetic-n"
                      value={
                        derivedTotalSyntheticN != null
                          ? roundToTwoDecimals(derivedTotalSyntheticN)
                          : '—'
                      }
                      readOnly
                      disabled
                      title="Derived from fertilizer applications"
                    />
                    <div className="field-helper-text">
                      Calculated from Fertilizer Applications (rate × N% ÷ planting acres).
                      Add products on the Fertilizer Applications tab.
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="cover-crop">Cover Crop:</label>
                    <select
                      id="cover-crop"
                      value={coverCropUsed}
                      onChange={(e) => setCoverCropUsed(e.target.value)}
                    >
                      {COVER_CROP_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              {mode === 'edit' && !readonlyCropType && (
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSubmitting}
                >
                  Delete Crop
                </button>
              )}
              <button
                type="submit"
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? mode === 'edit'
                    ? 'Updating...'
                    : 'Adding...'
                  : mode === 'edit'
                    ? 'Update Crop'
                    : 'Add Crop'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'fertilizer' && canManageApplications && (
          <div className="crop-picker-form">
            <div className="tab-content application-tab">
              <div className="application-tab-header">
                <h4>Fertilizer Applications</h4>
                <button
                  type="button"
                  className="btn-submit btn-sm"
                  onClick={openAddFertApp}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add
                </button>
              </div>
              {appsLoading ? (
                <p>Loading...</p>
              ) : fertilizerApps.length === 0 ? (
                <p className="empty-apps">No fertilizer applications yet.</p>
              ) : (
                <table className="application-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Rate</th>
                      <th>Acres</th>
                      <th>NI %</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {fertilizerApps.map((app) => (
                      <tr key={app.fertilizer_application_id}>
                        <td>{formatDisplayDate(app.the_date)}</td>
                        <td>{app.fertilizer?.fertilizer || '—'}</td>
                        <td>{Number(app.rate)}</td>
                        <td>{Number(app.acres)}</td>
                        <td>
                          {app.nitrification_inhibitor_percentage != null
                            ? Number(app.nitrification_inhibitor_percentage)
                            : '—'}
                        </td>
                        <td className="app-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => openEditFertApp(app)}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() =>
                              deleteFertApp(app.fertilizer_application_id)
                            }
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {error && <div className="error-message">{error}</div>}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}

        {activeTab === 'manure' && canManageApplications && (
          <div className="crop-picker-form">
            <div className="tab-content application-tab">
              <div className="application-tab-header">
                <h4>Manure Applications</h4>
                <button
                  type="button"
                  className="btn-submit btn-sm"
                  onClick={openAddManureApp}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add
                </button>
              </div>
              {appsLoading ? (
                <p>Loading...</p>
              ) : manureApps.length === 0 ? (
                <p className="empty-apps">No manure applications yet.</p>
              ) : (
                <table className="application-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Rate</th>
                      <th>Acres</th>
                      <th>N Content</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {manureApps.map((app) => (
                      <tr key={app.manure_application_id}>
                        <td>{formatDisplayDate(app.the_date)}</td>
                        <td>{app.manure?.manure || '—'}</td>
                        <td>{Number(app.rate)}</td>
                        <td>{Number(app.acres)}</td>
                        <td>{Number(app.n_content)}</td>
                        <td className="app-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => openEditManureApp(app)}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() =>
                              deleteManureApp(app.manure_application_id)
                            }
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {error && <div className="error-message">{error}</div>}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}

        {activeTab === 'tillage' && canManageApplications && (
          <div className="crop-picker-form">
            <div className="tab-content application-tab">
              <div className="application-tab-header">
                <h4>Tillage Events</h4>
                <button
                  type="button"
                  className="btn-submit btn-sm"
                  onClick={openAddTillageEvent}
                  disabled={expandedRowCount >= 20}
                  title={
                    expandedRowCount >= 20
                      ? 'T-DISC allows at most 20 expanded implement rows'
                      : 'Add tillage event'
                  }
                >
                  <FontAwesomeIcon icon={faPlus} /> Add
                </button>
              </div>
              <div className="field-helper-text" style={{ marginBottom: '0.75rem' }}>
                {tdiscPending
                  ? 'Pending carbon recalculation'
                  : tdiscDesignation
                    ? `T-DISC designation: ${tdiscDesignation}`
                    : null}
              </div>
              {appsLoading && tillageEvents.length === 0 ? (
                <p>Loading...</p>
              ) : tillageEvents.length === 0 ? (
                <p className="empty-apps">
                  No tillage events yet. Manual tillage on Crop Information is used
                  until events are added.
                </p>
              ) : (
                <table className="application-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Tillage Window</th>
                      <th>Operation</th>
                      <th>T-DISC Implements</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tillageEvents.map((event) => (
                      <tr key={event.tillage_event_id}>
                        <td>{formatDisplayDate(event.the_date)}</td>
                        <td>
                          {TILLAGE_WINDOW_LABELS[event.tillage_window] ||
                            event.tillage_window}
                        </td>
                        <td>{event.description}</td>
                        <td>
                          {(event.implements || []).join('; ') || '—'}
                        </td>
                        <td className="app-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => openEditTillageEvent(event)}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() =>
                              deleteTillageEvent(event.tillage_event_id)
                            }
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {error && <div className="error-message">{error}</div>}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {fertAppModalOpen && (
        <div className="modal-overlay nested-modal">
          <div className="modal-content application-form-modal">
            <div className="modal-header">
              <h2>
                {editingFertApp ? 'Edit' : 'Add'} Fertilizer Application
              </h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Product</label>
                <select
                  value={fertForm.fertilizer_id}
                  onChange={(e) =>
                    setFertForm((f) => ({ ...f, fertilizer_id: e.target.value }))
                  }
                >
                  {fertilizers.map((f) => (
                    <option key={f.fertilizer_id} value={f.fertilizer_id}>
                      {f.fertilizer}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <ApplicationDateField
                  value={fertForm.the_date}
                  onChange={(isoDate) =>
                    setFertForm((f) => ({ ...f, the_date: isoDate }))
                  }
                />
                <div className="field-helper-text">
                  Use the calendar for past application dates.
                </div>
              </div>
              <div className="form-group">
                <label>Rate (lbs/acre or gal/acre)</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={fertForm.rate}
                  onChange={(e) =>
                    setFertForm((f) => ({ ...f, rate: e.target.value }))
                  }
                />
              </div>
              <div className="form-group">
                <label>Acres</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={fertForm.acres}
                  onChange={(e) =>
                    setFertForm((f) => ({ ...f, acres: e.target.value }))
                  }
                />
              </div>
              <div className="form-group">
                <label>Nitrification Inhibitor (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={fertForm.nitrification_inhibitor_percentage}
                  onChange={(e) =>
                    setFertForm((f) => ({
                      ...f,
                      nitrification_inhibitor_percentage: e.target.value,
                    }))
                  }
                  placeholder="Optional"
                />
                <div className="field-helper-text">
                  Percent of this application treated with a nitrification inhibitor.
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setFertAppModalOpen(false)}
                disabled={fertFormSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={saveFertApp}
                disabled={fertFormSaving}
              >
                {fertFormSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {manureAppModalOpen && (
        <div className="modal-overlay nested-modal">
          <div className="modal-content application-form-modal">
            <div className="modal-header">
              <h2>{editingManureApp ? 'Edit' : 'Add'} Manure Application</h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Product</label>
                <select
                  value={manureForm.manure_id}
                  onChange={(e) =>
                    setManureForm((f) => ({ ...f, manure_id: e.target.value }))
                  }
                >
                  {manures.map((m) => (
                    <option key={m.manure_id} value={m.manure_id}>
                      {m.manure}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <ApplicationDateField
                  value={manureForm.the_date}
                  onChange={(isoDate) =>
                    setManureForm((f) => ({ ...f, the_date: isoDate }))
                  }
                />
                <div className="field-helper-text">
                  Use the calendar for past application dates.
                </div>
              </div>
              <div className="form-group">
                <label>Rate ({manureRateUnit})</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={manureForm.rate}
                  onChange={(e) =>
                    setManureForm((f) => ({ ...f, rate: e.target.value }))
                  }
                />
              </div>
              <div className="form-group">
                <label>Acres</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={manureForm.acres}
                  onChange={(e) =>
                    setManureForm((f) => ({ ...f, acres: e.target.value }))
                  }
                />
              </div>
              <div className="form-group">
                <label>N Content ({nContentUnit})</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={manureForm.n_content}
                  onChange={(e) =>
                    setManureForm((f) => ({ ...f, n_content: e.target.value }))
                  }
                />
                <div className="field-helper-text">
                  Enter N from manure test results (
                  {selectedManure?.form === 'liquid'
                    ? 'lbs N per 1000 gallons'
                    : 'lbs N per ton'}
                  ).
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setManureAppModalOpen(false)}
                disabled={manureFormSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={saveManureApp}
                disabled={manureFormSaving}
              >
                {manureFormSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tillageEventModalOpen && (
        <div className="modal-overlay nested-modal">
          <div className="modal-content application-form-modal">
            <div className="modal-header">
              <h2>{editingTillageEvent ? 'Edit' : 'Add'} Tillage Event</h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Date</label>
                <ApplicationDateField
                  value={tillageForm.the_date}
                  onChange={(isoDate) =>
                    setTillageForm((f) => ({ ...f, the_date: isoDate }))
                  }
                />
                <div className="field-helper-text">
                  Use the calendar for past operation dates.
                </div>
              </div>
              <div className="form-group">
                <label>Tillage Window</label>
                <select
                  value={tillageForm.tillage_window}
                  onChange={(e) =>
                    setTillageForm((f) => ({
                      ...f,
                      tillage_window: e.target.value,
                    }))
                  }
                >
                  {TILLAGE_WINDOWS.map((w) => (
                    <option key={w.value} value={w.value}>
                      {w.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Search Operation</label>
                <input
                  type="text"
                  value={operationSearch}
                  onChange={(e) => setOperationSearch(e.target.value)}
                  placeholder="Type to filter operations..."
                />
              </div>
              <div className="form-group">
                <label>Operation</label>
                <select
                  className="operation-list"
                  value={tillageForm.tdisc_operation_id}
                  onChange={(e) =>
                    setTillageForm((f) => ({
                      ...f,
                      tdisc_operation_id: e.target.value,
                    }))
                  }
                  size={4}
                >
                  {filteredTdiscOperations.map((op) => (
                    <option
                      key={op.tdisc_operation_id}
                      value={op.tdisc_operation_id}
                    >
                      {op.description}
                    </option>
                  ))}
                </select>
                <div
                  className={`selected-operation-display${
                    selectedTillageOperation ? '' : ' selected-operation-display--empty'
                  }`}
                >
                  <span className="selected-operation-label">Selected:</span>{' '}
                  {selectedTillageOperation?.description ||
                    'No operation selected'}
                </div>
              </div>
              <div className="form-group">
                <label>T-DISC Implements (read-only preview)</label>
                <textarea
                  readOnly
                  disabled
                  rows={2}
                  value={
                    selectedTillageOperation
                      ? selectedTillageOperation.implements
                          .map(
                            (impl) =>
                              `${impl.sequence}. ${impl.implement_name}`,
                          )
                          .join('\n')
                      : 'Select an operation to preview mapped implements'
                  }
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setTillageEventModalOpen(false)}
                disabled={tillageFormSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={saveTillageEvent}
                disabled={tillageFormSaving || projectedExpandedRows > 20}
              >
                {tillageFormSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && !readonlyCropType && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header">
              <h2>Confirm Delete</h2>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to remove this crop from the field?</p>
              <p className="warning-text">
                <strong>Warning:</strong> This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="btn-delete"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CropPickerModal;
